import React from 'react'
// libs
import { createRoot } from 'react-dom/client'
import { Provider, useSelector } from 'react-redux'
import { store } from 'store/store'
import { getFormValues } from 'redux-form'
// components
import TopMenu from './components/TopMenu'
import ExportMenu from './components/Menu'
import TriggerButton from './components/TriggerButton'
import { PopoverModal } from 'components/UI'
// hooks
import { useRouter } from 'hooks/useRouter'
import { useRiskExport } from 'hooks/risks/useRiskExport'
import { useApolloClient } from 'hooks/useApolloClient'
import { useRiskRelatedForms } from 'hooks/risks'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// types
import { Forms } from 'constants/forms'
// reports
import { REPORT_REGISTRY } from './reports'
// utils
import moment from 'moment'
import { getFieldValue } from './utils'

const MODAL_PROPS = {
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'right' as const
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'right' as const
  },
  PaperProps: {
    style: { marginTop: '24px' }
  }
}

const ExportButton: React.FC = () => {
  const router = useRouter()

  const { relatedForms } = useRiskRelatedForms()
  const { loading, generateDocument } = useRiskExport()

  const formValues = useSelector(getFormValues(Forms.RISK_TEMPLATE_FORM))

  const { client } = useApolloClient()

  const { riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const reportConfig = REPORT_REGISTRY[router.query.templateId as string]

  const relatedFormsMap = relatedForms
    .filter((item) => !item.multiple && item.riskId)
    .map((item) => ({
      riskId: item.riskId,
      riskTemplate: item.riskTemplate
    }))

  const getReportValues = (): Record<string, unknown> => {
    const values: Record<string, unknown> = {}

    Object.entries(reportConfig.fieldMap).forEach(([fieldId, mapEntry]) => {
      const rawField = (formValues as any)?.[fieldId]
      const fieldValue = rawField?.value

      if (typeof mapEntry === 'function') {
        const result = mapEntry(fieldValue)

        Object.assign(values, result)
      } else {
        const field = riskTemplate.fields.find((item) => item.id === fieldId)

        if (mapEntry && field) {
          values[mapEntry] = getFieldValue(fieldValue, field)
        }
      }
    })
    return values
  }

  const generatePdf = async () => {
    const { component: ReportComponent, filename, resolveAsync } = reportConfig

    let reportValues = getReportValues()

    if (resolveAsync) {
      const extra = await resolveAsync(reportValues, client)
      reportValues = { ...reportValues, ...extra }
    }

    const container = document.createElement('div')
    container.style.position = 'fixed'
    container.style.top = '0'
    container.style.left = '-9999px'
    container.style.width = '800px'
    container.style.overflow = 'hidden'
    container.style.pointerEvents = 'none'
    document.body.appendChild(container)

    const root = createRoot(container)

    try {
      root.render(
        <Provider store={store}>
          <ReportComponent initialValues={reportValues} />
        </Provider>
      )

      await new Promise<void>((resolve) => setTimeout(resolve, 600))

      const { default: html2pdf } = await import('html2pdf.js')
      const element = container.firstElementChild as HTMLElement

      const headerImg = new Image()
      headerImg.crossOrigin = 'anonymous'
      headerImg.src = '/images/department-of-justice.jpg'
      await new Promise<void>((resolve) => { headerImg.onload = () => resolve() })

      const PAGE_W = 210
      const PAGE_H = 297
      const MARGIN_X = 12
      const FOOTER_H = 14

      await html2pdf()
        .set({
          margin: [22, 0, FOOTER_H, 0],
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        })
        .from(element)
        .toPdf()
        .get('pdf')
        .then((pdf: any) => {
          const total = pdf.internal.getNumberOfPages()
          const dateStr = moment().format('DD MMM YYYY')

          for (let i = 1; i <= total; i++) {
            pdf.setPage(i)

            // header image — preserve aspect ratio
            const imgW = 79
            const imgH = imgW / (headerImg.naturalWidth / headerImg.naturalHeight)
            pdf.addImage(headerImg, 'JPEG', 24, 10, imgW, imgH)

            // footer
            pdf.setFontSize(10)
            pdf.setTextColor(60, 60, 60)
            const footerY = PAGE_H - FOOTER_H + 5
            pdf.text(`Incident Report`, MARGIN_X, footerY)
            pdf.text(`D23/888665 | Publication date: ${dateStr}`, MARGIN_X, footerY + 4)
            pdf.text(`Page ${i} of ${total}`, PAGE_W - MARGIN_X, footerY + 2, { align: 'right' })
          }

          pdf.save(filename)
        })

    } catch (err) {
      console.error('[generatePdf] failed:', err)
    } finally {
      root.unmount()
      document.body.removeChild(container)
    }
  }

  return (
    <PopoverModal {...MODAL_PROPS} trigger={<TriggerButton loading={loading} />}>
      {({ closePopup }) =>
        reportConfig ? (
          <TopMenu
            closePopup={closePopup}
            relatedForms={relatedFormsMap}
            generateDocument={generateDocument}
            generatePdf={generatePdf}
          />
        ) : (
          <ExportMenu closePopup={closePopup} relatedForms={relatedFormsMap} generateDocument={generateDocument} />
        )
      }
    </PopoverModal>
  )
}

export default ExportButton
